import { useEffect, useRef, useState } from "react";
import {
  equipmentDefinition,
  sameEquipmentKey,
  type EquipmentKey,
} from "@frontier/content/arsenal";
import type { Profile } from "@frontier/protocol/contracts";
import { EquipmentInventory } from "./EquipmentInventory";
import { EquipmentPresentation, equipmentDisplayParameters, equipmentValue } from "./EquipmentPresentation";
import { EquipmentArt } from "./EquipmentArt";
import type { EquipmentFocus } from "./equipment-focus";
import { blueprintSources } from "./blueprint-sources";
import { equipmentCount, mountedCount } from "./equipment-selection";
import "./equipment.css";
import "./equipment-detail.css";
import "./equipment-responsive.css";

export function EquipmentMarket({
  profile,
  busy,
  onBuy,
  onHangar,
}: {
  profile: Profile;
  busy: boolean;
  onBuy: (key: EquipmentKey) => Promise<Profile | void>;
  onHangar: (focus?: EquipmentFocus) => void;
}) {
  const [key, setKey] = useState<EquipmentKey>({
    kind: "system",
    id: "reactor",
  });
  const item = equipmentDefinition(key);
  const [receipt, setReceipt] = useState("");
  const sequence = useRef(0);
  const current = useRef({ profile, key });
  current.current = { profile, key };
  useEffect(() => {
    sequence.current++;
    setReceipt("");
    return () => { sequence.current++; };
  }, [profile.playerId, profile.hullId, profile.runId, key.kind, key.id]);
  const buy = async () => {
    const action = ++sequence.current;
    const before = profile;
    const selected = key;
    setReceipt("");
    let confirmed: Profile | void;
    try { confirmed = await onBuy(selected); }
    catch (error) {
      if (action === sequence.current && sameEquipmentKey(current.current.key, selected) &&
        current.current.profile.playerId === before.playerId && current.current.profile.hullId === before.hullId && current.current.profile.runId === before.runId)
        setReceipt(error instanceof Error ? error.message : "Покупка не подтверждена");
      return;
    }
    if (!confirmed || action !== sequence.current || !sameEquipmentKey(current.current.key, selected) ||
      [confirmed, current.current.profile].some(value => value.playerId !== before.playerId || value.hullId !== before.hullId || value.runId !== before.runId) ||
      equipmentCount(confirmed.arsenal, selected, 0) <= equipmentCount(before.arsenal, selected, 0)) return;
    setReceipt(`${equipmentDefinition(selected).name} MK I — на складе`);
  };
  const freeMki = equipmentCount(profile.arsenal, key, 0) - mountedCount(profile, key, 0);
  const known = profile.arsenal.knownBlueprints.some((saved) =>
    sameEquipmentKey(saved, key),
  );
  const shortfall = Math.max(0, item.recipeComponents - profile.components);
  const sources = blueprintSources(item);
  const unlockHint = sources.length > 0
    ? `Открой покупку: ${sources.join(" или ")}`
    : key.kind === "module" && key.id === "volley"
      ? "Открывается в обучении за помощь транспорту."
      : key.kind === "system"
        ? "Открывается через сезон. Условия — во вкладке «Сезон»."
        : "Покупка пока не открыта.";
  const summary = equipmentDisplayParameters(key, 0, profile.hullId).slice(0, 2)
    .map(parameter => `${parameter.label}: ${equipmentValue(parameter.value, parameter.unit)}`).join(" · ");
  return (
    <section
      className="equipment-fitting market-equipment"
      aria-label="Покупка оснащения"
    >
      <div className="fitting-workspace">
        <section className="fitting-inventory" aria-label="Каталог магазина">
          <div className="fitting-stock-heading">
            <h3>ОСНАЩЕНИЕ · MK I</h3>
          </div>
          <div className="fitting-stock-scroll">
            <EquipmentInventory
              context={{ kind: "cell", slot: 0 }}
              profile={profile}
              selected={{ key, tier: 0 }}
              showBlueprints
              market
              onInspect={(focus) => setKey(focus.key)}
            />
          </div>
        </section>
        <article
          className="fitting-detail"
          data-market-equipment={key.id}
          data-equipment-kind={key.kind}
          data-equipment-id={key.id}
          data-equipment-tier={0}
        >
          <header className="market-equipment-summary">
            <span className="market-equipment-summary-art"><EquipmentArt equipment={key} /></span>
            <div><h2>{item.name} MK I</h2><span>{summary}</span>{!known && <small>{unlockHint}</small>}</div>
          </header>
          <div className="fitting-detail-scroll">
            <EquipmentPresentation
              equipment={key}
              tier={0}
              profile={profile}
              status={known ? "ПОКУПКА ОТКРЫТА" : "ПОКУПКА ЗАКРЫТА"}
            />
            {!known && (
              <p className="fitting-source-hint">
                {unlockHint}
              </p>
            )}
            {shortfall > 0 && (
              <p className="fitting-action-reason">
                Не хватает {shortfall} компонентов.
              </p>
            )}
            <p className="market-explanation">Покупка добавляет одну копию MK I на склад. Установка и улучшение — в ангаре.</p>
          </div>
        </article>
      </div>
      <div className="fitting-detail-actions market-equipment-actions">
        {receipt && <p className="purchase-receipt" role="status">{receipt}</p>}
        <div className="market-purchase-price"><span>СТОИМОСТЬ · MK I</span><b>{item.recipeComponents.toLocaleString("ru-RU")} комп.</b>
          {!known ? <small>Покупка закрыта</small> : shortfall > 0 && <small>Не хватает {shortfall.toLocaleString("ru-RU")} комп.</small>}</div>
        <button className="fitting-install" data-equipment-action="buy" disabled={busy || !known || shortfall > 0}
          onClick={() => { if (!busy && known && shortfall === 0) void buy(); }}>КУПИТЬ MK I</button>
        <button onClick={() => onHangar(freeMki > 0 ? { key, tier: 0 } : undefined)}>
          {freeMki > 0 ? "УСТАНОВИТЬ · ВЫБРАТЬ ЯЧЕЙКУ →" : "В АНГАР →"}
        </button>
      </div>
    </section>
  );
}
